import React, { createContext, useContext, useMemo } from "react";
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";

import { z, type ZodTypeAny, type ZodError } from "zod";
import * as Schemas from "@generated-schemas";
import type { EndpointMap } from "@endpoint-type-map";
import type { InferResponse } from "@/types/registryTypes";
import { useConfig } from "@/contexts/ConfigContext";
import { getAuthState } from "@/contexts/AuthContext";
import { AppLogger } from '@/lib/logger';

const log = AppLogger.getLogger(import.meta);

// Guard to prevent multiple concurrent 401s from triggering logout more than once
let isLoggingOutDueTo401 = false;

// Default endpoint contract version - loaded from apiRoutes.yml
let DEFAULT_ENDPOINT_VERSION = "0.0.1";

// Base URL for the API Gateway
// In development, use empty string to leverage Vite's proxy
// In production, use the full URL from env or config
let API_BASE_URL = "";
let IS_DEVELOPMENT = false;
let API_TIMEOUT = 30000;

// Debug: Log the API base URL configuration
if (typeof window !== "undefined") {
  log.debug("Initial API_BASE_URL:", API_BASE_URL);
}

// --- Helpers ---

type EndpointSchemas = {
  request?: ZodTypeAny;
  response?: ZodTypeAny;
};

/**
 * Convert a camelCase endpoint name to PascalCase for schema name lookup.
 *
 * Examples:
 * - 'login' → 'Login'
 * - 'getUserByEmail' → 'GetUserByEmail'
 * - 'upsertDeviceManufacturer' → 'UpsertDeviceManufacturer'
 *
 * @param endpointName - The endpoint name in camelCase
 * @returns PascalCase version of the endpoint name
 */
function deriveSchemaName(endpointName: string): string {
  return endpointName.charAt(0).toUpperCase() + endpointName.slice(1);
}

/**
 * Schema cache for performance optimization.
 * Maps schemaKey to either the Zod schema (if found) or null (if not found).
 */
const schemaCache = new Map<string, ZodTypeAny | null>();

/**
 * Lookup a Zod schema by naming convention using the registry pattern.
 *
 * Convention: {PascalCaseEndpointName}Registry.requestSchema / .responseSchema
 *
 * Examples:
 * - endpointName='login', type='request' → LoginRegistry.requestSchema
 * - endpointName='getUserByEmail', type='response' → GetUserByEmailRegistry.responseSchema
 * - endpointName='upsertDevice', type='request' → UpsertDeviceRegistry.requestSchema
 *
 * Results are cached for O(1) lookups after first access.
 *
 * @param endpointName - The endpoint name in camelCase
 * @param type - Either 'request' or 'response'
 * @returns The Zod schema if found, or undefined if not found
 */
function lookupSchemaByConvention(endpointName: string, type: 'request' | 'response'): ZodTypeAny | undefined {
  const cacheKey = `${endpointName}:${type}`;

  if (schemaCache.has(cacheKey)) {
    const cached = schemaCache.get(cacheKey);
    return cached ?? undefined;
  }

  const pascalName = deriveSchemaName(endpointName);
  const registryName = `${pascalName}Registry`;
  const registry = (Schemas as any)[registryName];
  const schema = registry?.[type === 'request' ? 'requestSchema' : 'responseSchema'];

  if (!schema && IS_DEVELOPMENT) {
    log.debug(`No ${type} schema for '${endpointName}' (expected: ${registryName}.${type}Schema)`);
  }

  schemaCache.set(cacheKey, schema ?? null);
  return schema;
}

/**
 * Resolve a schema name (from YAML override) to the actual Zod schema.
 *
 * Special cases:
 * - 'z.any()' resolves to z.any()
 * - Registry-qualified names like 'LoginRegistry.responseSchema' are resolved via the registry
 * - Plain registry names like 'LoginRegistry' are not valid — must specify the sub-schema
 * - Undefined or null returns undefined
 *
 * @param schemaName - The schema name from YAML override (e.g., 'LoginRegistry.responseSchema' or 'z.any()')
 * @returns The Zod schema if found, or undefined if not found
 */
function resolveSchemaByName(schemaName?: string): ZodTypeAny | undefined {
  if (!schemaName) return undefined;

  if (schemaName === 'z.any()') {
    return z.any();
  }

  // Support registry-qualified names: 'LoginRegistry.responseSchema'
  const dotIndex = schemaName.indexOf('.');
  if (dotIndex !== -1) {
    const registryName = schemaName.slice(0, dotIndex);
    const subKey = schemaName.slice(dotIndex + 1);
    const registry = (Schemas as any)[registryName];
    const schema = registry?.[subKey];
    if (!schema) {
      log.warn(`Schema not found: ${schemaName}`);
    }
    return schema;
  }

  const schema = (Schemas as any)[schemaName];
  if (!schema) {
    log.warn(`Schema not found: ${schemaName}`);
  }

  return schema;
}

/**
 * Endpoint configuration mapping logical names to API paths and methods.
 * Path parameters are denoted with :paramName syntax.
 *
 * Optional schemas property allows YAML overrides for edge cases where
 * the naming convention doesn't apply (e.g., z.any() for file uploads).
 *
 * Optional mock property enables mock data for an endpoint:
 * - mock: true → Use convention-based mock discovery (MOCK_{SCREAMING_SNAKE_CASE})
 * - mock: "customFileName" → Load from specific mock file
 */
type EndpointConfig = {
  path: string;
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  // Optional per-endpoint contract version override.
  // If omitted, DEFAULT_ENDPOINT_VERSION is used.
  version?: string;
  // Optional schema overrides for non-standard endpoints.
  // If present, these take precedence over convention-based lookup.
  schemas?: {
    request?: string;   // Schema name (e.g., 'LoginRequestSchema' or 'z.any()')
    response?: string;  // Schema name (e.g., 'LoginResponseSchema' or 'z.any()')
  };
  // Optional mock data configuration
  // true = use convention, string = custom file name
  mock?: boolean | string;
  // Opt-in: for POST/PUT/PATCH endpoints whose backend body is STRICT (rejects unknown
  // fields), drop any keys consumed as path parameters from the request body so they are
  // sent only in the URL. Default (unset) keeps the existing behaviour (body = full variables).
  stripPathParamsFromBody?: boolean;
};

let endpointConfigs: Record<string, EndpointConfig> = {};

function getEndpointConfig(endpointName: string): EndpointConfig | undefined {
  return endpointConfigs[endpointName];
}

/**
 * Build the full URL for an endpoint, replacing path parameters.
 */
function buildUrl(endpointName: string, params?: Record<string, any>): string {
  const config = getEndpointConfig(endpointName);
  if (!config) {
    throw new Error(`Unknown endpoint: ${endpointName}`);
  }

  const paramsCopy = params;

  const contractVersion = config.version?.trim() || DEFAULT_ENDPOINT_VERSION;
  let path = config.path
    .replace("{endpointVersion}", contractVersion)
    .replace("{version}", contractVersion);

  // Replace path parameters (e.g., :manufacturerNumber)
  if (paramsCopy) {
    const pathParamPattern = /:(\w+)/g;
    path = path.replace(pathParamPattern, (_, paramName) => {
      if (paramsCopy[paramName] !== undefined) {
        const value = String(paramsCopy[paramName]);
        delete paramsCopy[paramName]; // Remove from params so it's not sent as query param
        return encodeURIComponent(value);
      }
      throw new Error(`Missing required path parameter: ${paramName}`);
    });
  }

  return `${API_BASE_URL}${path}`;
}

/**
 * Build query string from remaining params (for GET requests)
 */
function buildQueryString(params?: Record<string, any>): string {
  if (!params || Object.keys(params).length === 0) return "";
  const queryParts = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  return queryParts.length > 0 ? `?${queryParts.join("&")}` : "";
}

/**
 * Get request/response schemas for an endpoint using two-tier resolution:
 *
 * Tier 1: YAML overrides (if present in endpoint config)
 *   - Used for non-standard endpoints (e.g., z.any() for file uploads)
 *   - Takes precedence over convention
 *
 * Tier 2: Convention-based lookup (default)
 *   - Derives schema names from endpoint name and looks them up
 *   - Works for ~90% of endpoints without any YAML configuration
 *
 * @param endpointName - The endpoint name in camelCase
 * @returns EndpointSchemas with optional request/response schemas
 */
function getEndpointSchemas(endpointName: string): EndpointSchemas {
  const config = getEndpointConfig(endpointName);

  // Tier 1: Check YAML overrides first (if present)
  if (config?.schemas) {
    return {
      request: resolveSchemaByName(config.schemas.request),
      response: resolveSchemaByName(config.schemas.response),
    };
  }

  // Tier 2: Use convention-based lookup (default for all other endpoints)
  return {
    request: lookupSchemaByConvention(endpointName, 'request'),
    response: lookupSchemaByConvention(endpointName, 'response'),
  };
}

function formatZodErrors(error: ZodError): string {
  return error.issues
    .map((issue) => `${issue.path.join(".") || "(root)"}: ${issue.message}`)
    .join("; ");
}

// --- Mock Data System ---

/**
 * Convert a camelCase endpoint name to SCREAMING_SNAKE_CASE for mock export names.
 *
 * Examples:
 * - 'getDashboardKpis' → 'GET_DASHBOARD_KPIS'
 * - 'getUserByEmail' → 'GET_USER_BY_EMAIL'
 * - 'uploadInitiate' → 'UPLOAD_INITIATE'
 *
 * @param endpointName - The endpoint name in camelCase
 * @returns SCREAMING_SNAKE_CASE version with MOCK_ prefix
 */
function deriveMockExportName(endpointName: string): string {
  return endpointName
    .replace(/([A-Z])/g, '_$1')
    .toUpperCase()
    .replace(/^_/, '');
}

/**
 * Mock data cache for performance optimization.
 * Maps endpointName to either the mock data (if found) or null (if not found).
 */
const mockDataCache = new Map<string, any | null>();

/**
 * Dynamic mock discovery using import.meta.glob.
 * Loads all mock modules from mockData/**\/*Mock.ts (at project root)
 */
const mockDataModules = import.meta.glob<Record<string, any>>(
  '../../mockData/**/*Mock.ts',
  { eager: true }
);

/**
 * Lookup mock data by naming convention.
 *
 * Convention: {PascalCaseEndpointName} → MOCK_{SCREAMING_SNAKE_CASE_ENDPOINT_NAME}
 *
 * Examples:
 * - endpointName='getDashboardKpis' → MOCK_GET_DASHBOARD_KPIS
 * - endpointName='getUsers' → MOCK_GET_USERS
 * - endpointName='uploadInitiate' → MOCK_UPLOAD_INITIATE
 *
 * Results are cached for O(1) lookups after first access.
 *
 * @param endpointName - The endpoint name in camelCase
 * @returns The mock data if found, or undefined if not found
 */
function lookupMockDataByConvention(endpointName: string): any | undefined {
  // Check cache first
  if (mockDataCache.has(endpointName)) {
    const cached = mockDataCache.get(endpointName);
    return cached ?? undefined;
  }

  const mockExportName = `MOCK_${deriveMockExportName(endpointName)}`;

  // Search all mock modules for the export
  for (const [path, module] of Object.entries(mockDataModules)) {
    if (module[mockExportName]) {
      mockDataCache.set(endpointName, module[mockExportName]);
      if (IS_DEVELOPMENT) {
        log.debug(`Mock found: ${mockExportName} in ${path}`);
      }
      return module[mockExportName];
    }
  }

  // Not found - cache null to avoid repeated searches
  mockDataCache.set(endpointName, null);
  if (IS_DEVELOPMENT) {
    log.debug(`No mock found for '${endpointName}' (expected: ${mockExportName})`);
  }
  return undefined;
}

/**
 * Resolve mock data by custom file name (from YAML override).
 *
 * Searches for a module whose path contains the specified file name.
 *
 * @param mockFileName - The mock file name without .ts extension
 * @returns The mock data if found, or undefined if not found
 */
function resolveMockDataByFileName(mockFileName: string): any | undefined {
  for (const [path, module] of Object.entries(mockDataModules)) {
    if (path.includes(`${mockFileName}.ts`)) {
      // Return the first exported value from the module
      const exports = Object.values(module);
      return exports.length > 0 ? exports[0] : module;
    }
  }
  return undefined;
}

/**
 * Get mock data for an endpoint using two-tier resolution:
 *
 * Tier 1: YAML overrides (if present in endpoint config)
 *   - Used for custom mock file names
 *   - Takes precedence over convention
 *
 * Tier 2: Convention-based lookup (default)
 *   - Derives mock export name from endpoint name and looks it up
 *   - Works for ~90% of endpoints without any YAML configuration
 *
 * @param endpointName - The endpoint name in camelCase
 * @returns The mock data if found, or undefined if not found or mock not enabled
 */
function getEndpointMockData(endpointName: string): any | undefined {
  const config = getEndpointConfig(endpointName);
  if (!config?.mock) return undefined;

  // Tier 1: Custom file override
  if (typeof config.mock === 'string') {
    return resolveMockDataByFileName(config.mock);
  }

  // Tier 2: Convention-based lookup (when mock: true)
  return lookupMockDataByConvention(endpointName);
}

/**
 * Make a real HTTP request to the API Gateway
 */
async function fetchFromApi<TData>(
  endpointName: string,
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
  params?: Record<string, any>,
  body?: any,
  token?: string,
): Promise<TData> {
  // Build URL with path parameters replaced.
  // Use a mutable copy so buildUrl can delete path params from it;
  // the same copy is then used for buildQueryString so path params
  // are not duplicated in the query string.
  const mutableParams = params ? { ...params } : undefined;
  let url = buildUrl(endpointName, mutableParams);

  // For GET requests, append remaining params as query string
  if (method === "GET" && mutableParams) {
    url += buildQueryString(mutableParams);
  }

  // Debug logging
  log.debug(`API Request: ${method} ${url}`, { params, body, endpointName });

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    "Accept": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const fetchOptions: RequestInit = {
    method,
    headers,
    signal: AbortSignal.timeout(API_TIMEOUT),
    // For development with self-signed certs, you may need to handle this differently
    // credentials: "include",
  };

  if ((method === "POST" || method === "PUT" || method === "PATCH") && body !== undefined) {
    fetchOptions.body = JSON.stringify(body);
  }

  const response = await fetch(url, fetchOptions);

  if (!response.ok) {
    if (response.status === 401 && !isLoggingOutDueTo401) {
      isLoggingOutDueTo401 = true;
      log.warn("Received 401 — session expired, logging out");
      getAuthState().logout();
    }
    const errorData = await response.json().catch(() => ({}));
    const rawMessage = errorData?.message ?? errorData?.error;
    const errorMessage =
      (Array.isArray(rawMessage) ? rawMessage.join("; ") : rawMessage) ||
      `API error: ${response.status} ${response.statusText}`;
    throw new Error(errorMessage);
  }

  // Handle empty responses (204 No Content, or empty body)
  const contentLength = response.headers.get('content-length');
  const contentType = response.headers.get('content-type');

  const parseBody = async (): Promise<TData> => {
    if (response.status === 204 || contentLength === '0') {
      return {} as TData;
    }

    const text = await response.text();
    if (!text || text.trim() === '') {
      return {} as TData;
    }

    if (!contentType?.includes('application/json')) {
      try {
        return JSON.parse(text) as TData;
      } catch {
        return { message: text } as TData;
      }
    }

    try {
      return JSON.parse(text) as TData;
    } catch {
      return { message: text } as TData;
    }
  };

  const data = await parseBody();

  // Response validation (log-only, non-fatal) — single chokepoint for all callers
  const { response: responseSchema } = getEndpointSchemas(endpointName);
  if (responseSchema) {
    const parseResult = responseSchema.safeParse(data);
    if (!parseResult.success) {
      log.warn(
        `Response validation warning for ${endpointName}: ${formatZodErrors(parseResult.error)}`,
      );
    }
  }

  return data;
}

// --- Provider ---

export type ApiQueryServiceProviderProps = {
  /**
   * Auth token for API requests (e.g., JWT from authentication)
   */
  token?: string;
  children: React.ReactNode;
};

type ApiQueryContextValue = {
  token?: string;
};

const ApiQueryContext = createContext<ApiQueryContextValue | undefined>(
  undefined,
);

// Shared QueryClient for the entire app when using this provider
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

export const ApiQueryServiceProvider: React.FC<ApiQueryServiceProviderProps> = ({
  token,
  children,
}) => {
  // Get configuration from context
  const config = useConfig();
  const [isApiConfigReady, setIsApiConfigReady] = React.useState(false);

  // Update API settings from config when available
  React.useEffect(() => {
    if (config?.isLoading) {
      setIsApiConfigReady(false);
      return;
    }

    if (config?.serviceConfig && config?.apiConfig && !config.isLoading) {
      const appEnv = config.appEnv;
      IS_DEVELOPMENT = appEnv === 'development';
      // Endpoint version comes from apiRoutes config (contract versioning).
      DEFAULT_ENDPOINT_VERSION = config.apiConfig.endpointVersion;
      endpointConfigs = config.apiConfig.endpointConfigs ?? {};

      if (Object.keys(endpointConfigs).length === 0) {
        throw new Error(`No endpoint configurations found in apiRoutes.yml for VITE_APP_ENV=${appEnv}`);
      }
      if (!DEFAULT_ENDPOINT_VERSION || DEFAULT_ENDPOINT_VERSION.trim() === "") {
        throw new Error(`Missing endpointVersion in apiRoutes.yml for VITE_APP_ENV=${appEnv}`);
      }

      // Resolved by ConfigContext: empty under the Vite dev server so /api/* goes
      // through the proxy, otherwise service.yml's apiGateway.baseUrl.
      API_BASE_URL = config.apiBaseUrl;

      API_TIMEOUT = config.serviceConfig.apiGateway.timeout || 30000;

      log.info('Configuration loaded:', {
        appEnv,
        defaultEndpointVersion: DEFAULT_ENDPOINT_VERSION,
        apiGatewayVersion: config.serviceConfig.apiGateway.version,
        apiBaseUrl: API_BASE_URL || '(using Vite proxy)',
        endpointCount: Object.keys(endpointConfigs).length,
        timeout: config.serviceConfig.apiGateway.timeout,
      });

      // Log schema coverage report in development mode
      if (appEnv === 'development') {
        const endpointNames = Object.keys(endpointConfigs);
        let requestSchemaCount = 0;
        let responseSchemaCount = 0;

        for (const name of endpointNames) {
          const schemas = getEndpointSchemas(name);
          if (schemas.request) requestSchemaCount++;
          if (schemas.response) responseSchemaCount++;
        }

        const totalCount = endpointNames.length;
        const coverage = ((requestSchemaCount + responseSchemaCount) / (totalCount * 2) * 100).toFixed(1);

        log.info('Schema coverage report:', {
          totalEndpoints: totalCount,
          requestSchemasCount: requestSchemaCount,
          responseSchemasCount: responseSchemaCount,
          coveragePercentage: `${coverage}%`,
          schemasCached: schemaCache.size,
        });

        // Log mock data coverage report
        let mockedCount = 0;
        const mockedNames: string[] = [];

        for (const name of endpointNames) {
          const mockData = getEndpointMockData(name);
          if (mockData) {
            mockedCount++;
            mockedNames.push(name);
          }
        }

        log.info('Mock data coverage report:', {
          totalEndpoints: totalCount,
          mockedEndpoints: mockedCount,
          coverage: `${((mockedCount / totalCount) * 100).toFixed(1)}%`,
          endpoints: mockedNames,
          mocksCached: mockDataCache.size,
        });
      }

      setIsApiConfigReady(true);
    }
  }, [config]);

  const value = useMemo<ApiQueryContextValue>(
    () => ({ token }),
    [token],
  );

  return (
    <ApiQueryContext.Provider value={value}>
      <QueryClientProvider client={queryClient}>
        {config?.isLoading || !isApiConfigReady ? (
          <div className="flex h-screen items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-lg font-semibold">Loading configuration...</div>
              <div className="text-sm text-muted-foreground">Initializing environment</div>
            </div>
          </div>
        ) : config?.error ? (
          <div className="flex h-screen items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-lg font-semibold text-destructive">Configuration Error</div>
              <div className="text-sm text-muted-foreground">{config.error.message}</div>
            </div>
          </div>
        ) : (
          children
        )}
      </QueryClientProvider>
    </ApiQueryContext.Provider>
  );
};

function useApiQueryContext(): ApiQueryContextValue {
  const ctx = useContext(ApiQueryContext);
  if (!ctx)
    throw new Error(
      "useApiQueryContext must be used within an ApiQueryServiceProvider",
    );
  return ctx;
}

// --- Public hooks built on TanStack Query ---

/**
 * useApiQuery
 *
 * Example:
 * const result = useApiQuery('getDeviceType', { deviceTypeNumber: 1000000001 })
 * const { data, error, isLoading } = result
 *
export function useApiQuery<K extends keyof EndpointMap>(
  endpointName: K,
  ...
): UseQueryResult<InferResponse<EndpointMap[K]>, Error>
*/
export function useApiQuery<K extends keyof EndpointMap>(
  endpointName: K,
  params?: any,
  options?: Omit<UseQueryOptions<InferResponse<EndpointMap[K]>, Error, InferResponse<EndpointMap[K]>, any[]>, "queryKey" | "queryFn">,
): UseQueryResult<InferResponse<EndpointMap[K]>, Error>;
export function useApiQuery<TData = any>(
  endpointName: string,
  params?: any,
  options?: Omit<UseQueryOptions<TData, Error, TData, any[]>, "queryKey" | "queryFn">,
): UseQueryResult<TData, Error>;
export function useApiQuery<TData = any>(
  endpointName: string,
  params?: any,
  options?: Omit<
    UseQueryOptions<TData, Error, TData, any[]>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<TData, Error> {
  const { token } = useApiQueryContext();
  const { request: requestSchema, response: responseSchema } = getEndpointSchemas(endpointName);
  const config = getEndpointConfig(endpointName);

  return useQuery<TData, Error, TData, any[]>({
    queryKey: ["api", endpointName, params],
    enabled: (options?.enabled ?? true) && !!config,
    retry: options?.retry ?? 1,
    // Forward polling / refresh options so callers can e.g. poll while a device's
    // provisioning is still `requested` (the Prov service flips it asynchronously).
    refetchInterval: options?.refetchInterval,
    refetchIntervalInBackground: options?.refetchIntervalInBackground,
    staleTime: options?.staleTime,
    queryFn: async () => {
      if (!config) {
        throw new Error(`Unknown endpoint: ${endpointName}`);
      }

      // Mock intercept: check for mock data before making API call
      const mockData = getEndpointMockData(endpointName);
      if (mockData) {
        log.info(`[MOCK] Using mock data for '${endpointName}'`);

        // Simulate network delay in development
        if (IS_DEVELOPMENT) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }

        // Validate against response schema if available
        if (responseSchema) {
          const parseResult = responseSchema.safeParse(mockData);
          if (!parseResult.success) {
            log.warn(
              `Mock validation warning for ${endpointName}:`,
              formatZodErrors(parseResult.error),
            );
          }
        }

        return mockData as TData;
      }

      // Zod-based request validation (blocking)
      if (requestSchema) {
        // Request schemas expect params, query and body. Path params and query params arrive
        // together in `params`, so validate it against both — z.object() strips the keys that
        // don't belong to each (e.g. path params are dropped from the query object).
        const parseResult = requestSchema.safeParse({ params: params ?? {}, query: params ?? {}, body: {} });
        if (!parseResult.success) {
          throw new Error(
            `Request validation failed: ${formatZodErrors(parseResult.error)}`,
          );
        }
      }

      // Make real API call (response validation happens inside fetchFromApi)
      return await fetchFromApi<TData>(
        endpointName,
        config.method,
        params,
        undefined,
        token,
      );
    },
    ...options,
  });
}

/**
 * useApiMutation
 *
 * Example:
 * const mutation = useApiMutation('upsertDeviceType', 'POST')
 * mutation.mutate(formData)
 *
 * For GET mutations (like archive operations):
 * const mutation = useApiMutation('archiveDevice', 'GET')
 * mutation.mutate({ deviceNumber: 1000000001 })
 */
export function useApiMutation<K extends keyof EndpointMap>(
  endpointName: K,
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
  options?: UseMutationOptions<InferResponse<EndpointMap[K]>, Error, any, unknown> & { invalidates?: string[] },
): UseMutationResult<InferResponse<EndpointMap[K]>, Error, any, unknown>;
export function useApiMutation<TData = any, TVariables = any>(
  endpointName: string,
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
  options?: UseMutationOptions<TData, Error, TVariables, unknown> & { invalidates?: string[] },
): UseMutationResult<TData, Error, TVariables, unknown>;
export function useApiMutation<TData = any, TVariables = any>(
  endpointName: string,
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
  options?: UseMutationOptions<TData, Error, TVariables, unknown> & {
    invalidates?: string[];
  },
): UseMutationResult<TData, Error, TVariables, unknown> {
  const { token } = useApiQueryContext();
  const { request: requestSchema, response: responseSchema } = getEndpointSchemas(endpointName);
  const config = getEndpointConfig(endpointName);

  const { invalidates, ...mutationOptions } = options ?? {};

  return useMutation<TData, Error, TVariables, unknown>({
    mutationKey: ["api", endpointName, method],
    mutationFn: async (variables: TVariables) => {
      if (!config) {
        throw new Error(`Unknown endpoint: ${endpointName}`);
      }

      // Mock intercept: check for mock data before making API call
      const mockData = getEndpointMockData(endpointName);
      if (mockData) {
        log.info(`[MOCK] Using mock data for '${endpointName}'`);

        // Simulate network delay for mutations (longer than queries)
        if (IS_DEVELOPMENT) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        // Validate against response schema if available
        if (responseSchema) {
          const parseResult = responseSchema.safeParse(mockData);
          if (!parseResult.success) {
            log.warn(
              `Mock validation warning for ${endpointName}:`,
              formatZodErrors(parseResult.error),
            );
          }
        }

        return mockData as TData;
      }

      // For GET mutations, pass variables as params; for POST/PUT, pass as body
      // Special handling: if POST/PUT endpoint has path parameters, extract them
      let params: Record<string, any> | undefined;
      let body: any;

      if (method === "GET") {
        params = variables as Record<string, any>;
        body = undefined;
      } else if (method === "POST" || method === "PUT" || method === "PATCH") {
        // Check if endpoint path has path parameters (e.g., :userNumber, :jobRoleNumber)
        const pathParamPattern = /:(\w+)/g;
        const pathParams = Array.from(config.path.matchAll(pathParamPattern)).map(match => match[1]);

        if (pathParams.length > 0 && variables && typeof variables === 'object') {
          // Extract path parameters from variables
          params = {};
          const varsObj = variables as Record<string, any>;
          for (const paramName of pathParams) {
            if (varsObj[paramName] !== undefined) {
              params[paramName] = varsObj[paramName];
            }
          }
        }

        body = variables;

        // Opt-in (stripPathParamsFromBody): keep path params out of the body for
        // strict-body endpoints that reject unknown fields. Default behaviour is unchanged.
        if (
          config.stripPathParamsFromBody &&
          pathParams.length > 0 &&
          body &&
          typeof body === 'object'
        ) {
          const cleanBody = { ...(body as Record<string, any>) };
          for (const paramName of pathParams) {
            delete cleanBody[paramName];
          }
          body = cleanBody;
        }
      } else if (method === "DELETE") {
        const pathParamPattern = /:(\w+)/g;
        const pathParams = Array.from(config.path.matchAll(pathParamPattern)).map(match => match[1]);
        if (pathParams.length > 0 && variables && typeof variables === 'object') {
          params = {};
          const varsObj = variables as Record<string, any>;
          for (const paramName of pathParams) {
            if (varsObj[paramName] !== undefined) {
              params[paramName] = varsObj[paramName];
            }
          }
        }
        body = undefined;
      }

      // Zod-based request validation (blocking)
      if (requestSchema) {
        const parseResult = requestSchema.safeParse({ params: params ?? {}, query: params ?? {}, body: body ?? {} });
        if (!parseResult.success) {
          throw new Error(
            `Request validation failed: ${formatZodErrors(parseResult.error)}`,
          );
        }
      }

      // Make real API call (response validation happens inside fetchFromApi)
      return await fetchFromApi<TData>(
        endpointName,
        method,
        params,
        body,
        token,
      );
    },
    onSuccess: (data, variables, context) => {
      if (invalidates && invalidates.length > 0) {
        for (const queryEndpoint of invalidates) {
          queryClient.invalidateQueries({ queryKey: ["api", queryEndpoint] });
        }
      }
      mutationOptions?.onSuccess?.(data, variables, context);
    },
    onError: mutationOptions?.onError,
    onSettled: mutationOptions?.onSettled,
    retry: mutationOptions?.retry ?? 0,
  });
}

/**
 * Export the query client for manual cache operations
 */
export { queryClient };
